import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SplitLines } from "../components/motion/SplitLines";
import { Reveal, EASE } from "../components/motion/Reveal";
import { ESSAYS } from "../data/study";

export default function Essay() {
  const { slug } = useParams();
  const idx = ESSAYS.findIndex((e) => e.slug === slug);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  if (idx === -1) return <Navigate to="/study" replace />;
  const essay = ESSAYS[idx];
  const next = ESSAYS[(idx + 1) % ESSAYS.length];

  return (
    <article data-testid={`essay-${essay.slug}`} className="mx-auto max-w-7xl px-5 pt-32 pb-28 sm:px-8 lg:pt-40">
      <motion.div
        data-testid="reading-progress-bar"
        style={{ scaleX }}
        className="fixed inset-x-0 top-0 z-[60] h-[2px] origin-left bg-terracotta"
        aria-hidden="true"
      />

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-32">
            <Link to="/study#essays" data-testid="essay-back" className="link-underline inline-flex items-center gap-2 text-xs font-medium text-ink-soft">
              <ArrowLeft size={14} /> All essays
            </Link>
            <p className="mt-10 eyebrow">Essay · {essay.readingTime}</p>
            <nav aria-label="In this essay" className="mt-8 hidden lg:block">
              <ul className="space-y-2.5 border-l border-ink/10">
                {essay.sections.map((s, i) => (
                  <li key={s.heading}>
                    <a href={`#section-${i + 1}`} className="link-underline block pl-4 text-sm text-ink-soft hover:text-ink">
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-7 lg:col-start-5">
          <SplitLines
            lines={[essay.title]}
            delay={0.15}
            className="font-serif text-4xl leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          />
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className="mt-5 font-serif text-2xl italic leading-snug text-ink-soft"
          >
            {essay.subtitle}
          </motion.p>

          <div className="mt-16 space-y-16">
            {essay.sections.map((s, i) => (
              <Reveal key={s.heading}>
                <section id={`section-${i + 1}`} data-testid={`essay-section-${i + 1}`} className="scroll-mt-32">
                  <h2 className="font-serif text-3xl leading-snug text-ink">{s.heading}</h2>
                  <div className="prose-warm mt-6 max-w-2xl text-base leading-[1.85] text-ink-soft sm:text-lg">
                    {s.paragraphs.map((p, j) => (
                      <p key={j} className={i === 0 && j === 0 ? "drop-cap" : ""}>
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              </Reveal>
            ))}
          </div>

          <Link
            to={`/study/essays/${next.slug}`}
            data-testid="essay-next"
            className="group mt-24 flex items-center justify-between border hairline bg-white p-8 transition-colors duration-500 hover:bg-parchment-deep"
          >
            <span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">Next essay</span>
              <span className="mt-2 block font-serif text-3xl leading-tight text-ink">{next.title}</span>
            </span>
            <ArrowRight size={20} className="text-ink transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </article>
  );
}
